import React, { useEffect, useState } from 'react'
import { Button, Form, InputNumber, Select } from 'antd'
import { DashboardSources } from '../../../constants/DashboardConf'
import { useTranslation } from 'react-i18next'
import { JourneyItem, JourneyTwoRowItems, JourneyWrapper } from '../dashboard.styled'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import Filter from './Filter'

function SankeySetting (props) {
  const { defPlaceholder, defConfig, dataSourceTree, dataSourceTableMapping, tableJoinKeyMapping, form, show, editData, columnMapping } = props
  const { t: ln_t } = useTranslation()
  const [journeyCount, setJourneyCount] = useState([1])
  const [filterCounter, setFilterCounter] = useState({})
  const formVal = Form.useWatch() || {}

  const journeyFields = num => ['dataSourceId', 'tableName', 'targetColumn', 'valueColumn', ...num === 1 ? ['sourceColumn'] : ['joinKeyColumn']]
  const handleAddJourney = async () => {
    await form.validateFields(journeyCount?.map(num => journeyFields(num).map(field => `${num}-${field}`)).flat())
    setJourneyCount([...journeyCount, Math.max(...journeyCount) + 1])
  }
  const handleDelJourney = journey => {
    setJourneyCount(journeyCount.filter(i => i !== journey))
    form.setFieldsValue(journeyFields(journey).reduce((all, field) => ({ ...all, [`${journey}-${field}`]: undefined }), {}))
  }
  const resetFields = (journey, excludeTableName) => {
    const fieldsToReset = Object.keys(formVal).filter(i => {
      const [journeyNum, field] = i.split('-')
      return Number(journeyNum) === journey && field !== 'dataSourceId' && (!excludeTableName || field !== 'tableName')
    })
    form.setFieldsValue(fieldsToReset?.reduce((all, field) => ({ ...all, [field]: undefined }), {}))
  }

  const handleAddFilter = journey => {
    const newFilterNum = Math.max(...filterCounter[journey]?.length ? filterCounter[journey] : [0]) + 1
    setFilterCounter({ ...filterCounter, [journey]: [...filterCounter[journey] || [], newFilterNum] })
    form.setFieldValue(`${journey}-${newFilterNum}-operator`, 'Equal')
  }
  const handleDelFilter = (journey, key) => {
    setFilterCounter(prev => ({ ...prev, [journey]: prev[journey]?.filter(i => i !== key) }))
    form.setFieldsValue({ [`${journey}-${key}-column`]: undefined, [`${journey}-${key}-value`]: undefined, [`${journey}-${key}-operator`]: undefined })
  }

  const getColumnOpt = journey => tableJoinKeyMapping[`${formVal[`${journey}-dataSourceId`]}-${formVal[`${journey}-tableName`]}`]
  const getFilterColMapping = journey => {
    const uniq = `${formVal[`${journey}-dataSourceId`]}^@^${formVal[`${journey}-tableName`]}^@^`
    return Object.entries(columnMapping)?.filter(([key, val]) => key.startsWith(uniq)).reduce((all, [key, val]) => ({ ...all, [key.replace(uniq, '')]: val }), {})
  }

  useEffect(() => {
    setJourneyCount(editData?.UISetting?.journeyCount || [1])
    setFilterCounter(editData?.UISetting?.filterCounter || {})
  }, [show])

  return (
    <>
      <Form.Item {...defConfig('source', 'select')} initialValue={DashboardSources.DATASOURCE}>
        <Select options={[{ value: DashboardSources.DATASOURCE, label: ln_t('dashboard.firstParty') }]} disabled />
      </Form.Item>
      <Form.Item {...defConfig('dataRange', 'input', false)}>
        <InputNumber placeholder={defPlaceholder('dataRange', 'input')} precision={0} min={0} style={{ width: '100%' }} />
      </Form.Item>
      <div>{ln_t('dashboard.journey')}</div>
      <JourneyWrapper>
        {journeyCount?.map(journey => <JourneyItem key={journey}>
          <div style={{ fontWeight: 600, display: 'flex', height: '32px', alignItems: 'center' }}>
            {ln_t('dashboard.journey')} {journey}
            {journeyCount.length > 1 && journey === journeyCount[journeyCount.length - 1] &&
              <Button type="link" icon={<DeleteOutlined />} style={{ color: 'var(--primary', marginLeft: 'auto' }} onClick={() => handleDelJourney(journey)}>
                {ln_t('util.delete')}
              </Button>}
          </div>

          <JourneyTwoRowItems>
            <Form.Item {...defConfig('dataSource', 'select')} name={`${journey}-dataSourceId`}>
              <Select
                showSearch
                placeholder={defPlaceholder('dataSource', 'select')}
                options={dataSourceTree?.map(i => ({ value: i.dataSourceId, label: i.dataSourceName }))}
                optionFilterProp="children"
                onChange={() => resetFields(journey)}
              />
            </Form.Item>
            <Form.Item {...defConfig('tableName', 'select')} name={`${journey}-tableName`}>
              <Select
                showSearch
                placeholder={defPlaceholder('tableName', 'select')}
                options={dataSourceTableMapping[formVal[`${journey}-dataSourceId`]]}
                optionFilterProp="children"
                disabled={!formVal[`${journey}-dataSourceId`]}
                onChange={() => resetFields(journey, true)}
              />
            </Form.Item>
          </JourneyTwoRowItems>
          <JourneyTwoRowItems>
            <Form.Item {...defConfig('startingPoint', 'select', journey === 1)} name={`${journey}-sourceColumn`}>
              <Select
                showSearch
                placeholder={journey === 1 ? defPlaceholder('startingPoint', 'select') : ''}
                options={getColumnOpt(journey)}
                optionFilterProp="children"
                disabled={!formVal[`${journey}-tableName`] || journey !== 1}
                {...journey === 1 ? {} : { suffixIcon: false }}
              />
            </Form.Item>
            <Form.Item {...defConfig('endPoint', 'select')} name={`${journey}-targetColumn`}>
              <Select
                showSearch
                placeholder={defPlaceholder('endPoint', 'select')}
                options={getColumnOpt(journey)}
                optionFilterProp="children"
                disabled={!formVal[`${journey}-tableName`]}
              />
            </Form.Item>
          </JourneyTwoRowItems>
          <JourneyTwoRowItems>
            <Form.Item {...defConfig('count', 'select')} name={`${journey}-valueColumn`}>
              <Select
                showSearch
                placeholder={defPlaceholder('count', 'select')}
                options={getColumnOpt(journey)}
                optionFilterProp="children"
                disabled={!formVal[`${journey}-tableName`]}
              />
            </Form.Item>
            <Form.Item name={`${journey}-valueColumn`} label={`${ln_t('dashboard.joinKeyValue')}${journey + 1}`}>
              <Select suffixIcon={false} options={getColumnOpt(journey)} disabled />
            </Form.Item>
          </JourneyTwoRowItems>
          {journey !== 1 && <Form.Item {...defConfig('joinKey', 'select')} name={`${journey}-joinKeyColumn`} label={`${ln_t('dashboard.joinKeyValue')}${journey - 1}`}>
              <Select
                showSearch
                placeholder={defPlaceholder('joinKey', 'select')}
                options={getColumnOpt(journey)}
                optionFilterProp="children"
                disabled={!formVal[`${journey}-tableName`]}
              />
            </Form.Item>}

          {/* ----- filter ----- */}
          <Filter
            {...props}
            uniqueKey={journey}
            filterOpt={getColumnOpt(journey)}
            counter={filterCounter[journey] || []}
            handleDelCounter={(...args) => handleDelFilter(journey, ...args)}
            handleAddCounter={() => handleAddFilter(journey)}
            columnMapping={getFilterColMapping(journey)}
            hideFilterLogic
          />
          {/* {filterCounter[journey]?.map(key => <React.Fragment key={key}>
            <CounterTitle>
              {ln_t('dashboard.filter')} {key}
              <DeleteTwoTone style={{ cursor: 'pointer' }} twoToneColor="#707070" onClick={() => handleDelFilter(journey, key)} />
            </CounterTitle>
            <HorizontalFormWrapper>
              <Form.Item name={`${journey}-${key}-column`} rules={[{ required: true, message: defPlaceholder('column', 'select') }]}>
                <Select
                  showSearch
                  placeholder={defPlaceholder('column', 'select')}
                  options={getColumnOpt(journey)}
                  optionFilterProp="children"
                  disabled={!formVal[`${journey}-tableName`]}
                />
              </Form.Item>
              <Form.Item name={`${journey}-${key}-operator`} rules={[{ required: true, message: defPlaceholder('operator', 'input') }]} >
                <Select options={[{ value: 'Equal', label: ln_t('criteria.logics.equal') }]} disabled suffixIcon={false} />
              </Form.Item>
              <Form.Item name={`${journey}-${key}-value`} rules={[{ required: true, message: defPlaceholder('value', 'input') }]}>
                <Input placeholder={defPlaceholder('value', 'input')} />
              </Form.Item>
            </HorizontalFormWrapper>
          </React.Fragment>)}
          <Button type="default" style={{ width: '100%', marginTop: '8px' }} icon={<PlusOutlined />} onClick={() => handleAddFilter(journey)}>
            {ln_t('util.add')} {ln_t('dashboard.filter')}
          </Button> */}
        </JourneyItem>)}

        {journeyCount.length < 4 && <div style={{ width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button onClick={handleAddJourney} style={{ transform: 'rotate(-90deg)', padding: '2px 100px' }} icon={<PlusOutlined />}>
            {ln_t('dashboard.addJourney')}
          </Button>
        </div>}
      </JourneyWrapper>
    </>
  )
}

export default SankeySetting
